import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import useDidMountEffect from "../utils/useDidMountEffect";

import NewClientModal from "./NewClientModal";

const ClientsTable = (props) => {
  const { clients, setClients, selectedClient, setSelectedClient } = props;
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [newClientModal, setNewClientModal] = useState("");

  const removeClient = (clientId) => {
    fetch(`/clients/${clientId}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
    });

    setClients(clients.filter((client) => client.id !== clientId));

    if (selectedClient && selectedClient.id === clientId) {
      setSelectedClient(null);
    }
  };

  const fetchClients = (search) => {
    let url = "/clients";
    if (search) {
      url += "?search=" + search;
    }

    fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setClients(data.clients);
      });
  };

  useDidMountEffect(() => {
    fetchClients(debouncedSearch);
  }, [debouncedSearch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search, 500]);

  useEffect(() => {
    setSelectedClient(null);
  }, [clients]);

  const clientsTable = clients.map((client, index) => (
    <tr
      key={index}
      className={`shadow ${
        selectedClient && selectedClient.id == client.id
          ? "bg-primaryYellow500"
          : "hover:bg-gray-50"
      }`}
      onClick={() => {
        setSelectedClient(client);
      }}
    >
      <td className="p-3">
        <div className="font-medium">{client.name}</div>
        <div className="text-xs text-gray-500">{client.email}</div>
      </td>
      <td className="p-3 text-right whitespace-nowrap">
        <div className="flex items-center justify-end gap-1 text-gray-700">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
            />
          </svg>
          {client.user_count}/{client.user_limit}
        </div>
      </td>
      <td className="p-3 text-right">
        <button
          onClick={() => {
            removeClient(client.id);
          }}
          className="cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="size-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
            />
          </svg>
        </button>
      </td>
    </tr>
  ));

  return (
    <>
      {newClientModal && (
        <NewClientModal
          setNewClientModal={setNewClientModal}
          setClients={setClients}
          clients={clients}
        />
      )}
      <div className="w-96 flex flex-col gap-2">
        <button
          className="flex items-center gap-2 bg-black text-white px-4 py-2 text-sm font-medium"
          onClick={() => setNewClientModal(true)}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M12 4v16m8-8H4" />
          </svg>
          Dodaj Konto Klienckie
        </button>
        <div className="relative">
          <input
            type="text"
            placeholder="Wyszukaj..."
            className="pl-10 pr-8 py-1.5 border border-gray-300 text-sm w-full"
            onChange={(e) => setSearch(e.target.value)}
          />
          <svg
            className="w-4 h-4 absolute left-2.5 top-2 text-gray-400"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
          >
            <path d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1010.5 18a7.5 7.5 0 006.15-3.35z" />
          </svg>
        </div>
        <div className="h-full overflow-auto max-h-[70vh]">
          <table className="w-full text-left border-separate border-spacing-y-4">
            <tbody>{clientsTable}</tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ClientsTable;

ClientsTable.propTypes = {
  selectedClient: PropTypes.object,
  clients: PropTypes.array,
  setClients: PropTypes.func,
  setSelectedClient: PropTypes.func,
};
